import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { PaymentServiceWithVersionsResponseType } from '../../../../../api/types/payments/payment-template';
import { PaymentServicesTable } from '../../../../payments/tabs/componenets/payment-services-table';
import { createStandingOrderBankTransferCancelReasonOperation } from '../operations/create-standing-order-bank-transfer-cancel-reason-operation';

const CANCELLED_REASON_MAX_LENGTH = 20;

const resolvePaymentServiceVersionId = (
  service: PaymentServiceWithVersionsResponseType,
): number | null => {
  if (service.versionId) {
    return service.versionId;
  }
  if (service.versions && service.versions.length > 0) {
    const latestVersion = service.versions.reduce((latest, current) =>
      current.id > latest.id ? current : latest,
    );
    return latestVersion.id;
  }
  return null;
};

interface AddStandingOrderBankTransferCancelReasonDialogProps {
  visible: boolean;
  onHide: () => void;
  onCreated: () => void;
}

export const AddStandingOrderBankTransferCancelReasonDialog: React.FC<
  AddStandingOrderBankTransferCancelReasonDialogProps
> = ({ visible, onHide, onCreated }) => {
  const [cancelledReason, setCancelledReason] = useState<string>('');
  const [selectedService, setSelectedService] =
    useState<PaymentServiceWithVersionsResponseType | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  // Bumping this key remounts the shared PaymentServicesTable so its internal
  // filter / pagination state start fresh every time the dialog reopens.
  const [tableKey, setTableKey] = useState<number>(0);

  useEffect(() => {
    if (visible) {
      setCancelledReason('');
      setSelectedService(null);
      setTableKey((k) => k + 1);
    }
  }, [visible]);

  const trimmedReason = cancelledReason.trim();
  const isReasonValid =
    trimmedReason.length >= 1 &&
    trimmedReason.length <= CANCELLED_REASON_MAX_LENGTH;

  const resolvedVersionId = selectedService
    ? resolvePaymentServiceVersionId(selectedService)
    : null;

  const isApproveDisabled =
    !selectedService || !isReasonValid || !resolvedVersionId || loading;

  const approveTooltip = !selectedService
    ? 'יש לבחור שירות תשלום'
    : !isReasonValid
      ? `יש להקליד סיבה (עד ${CANCELLED_REASON_MAX_LENGTH} תווים)`
      : !resolvedVersionId
        ? 'לשרות התשלום שנבחר אין גרסת מחיר זמינה'
        : 'הוספת סיבת ביטול';

  const handleApprove = async () => {
    if (!selectedService || !isReasonValid || !resolvedVersionId) {
      return;
    }

    await createStandingOrderBankTransferCancelReasonOperation(
      {
        cancelledReason: trimmedReason,
        paymentServiceId: selectedService.id,
        paymentServiceVersionId: resolvedVersionId,
      },
      setLoading,
      () => {
        onCreated();
        onHide();
      },
    );
  };

  const handleHide = () => {
    if (loading) return;
    onHide();
  };

  return (
    <Dialog
      header="הוספת סיבת כישלון להוראת קבע בנקאית"
      visible={visible}
      onHide={handleHide}
      style={{ width: '900px' }}
      closable={!loading}
      modal
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          direction: 'rtl',
        }}
      >
        <div>
          <label
            htmlFor="cancel-reason-input"
            style={{ display: 'block', marginBottom: '8px' }}
          >
            סיבת כישלון:
          </label>
          <InputText
            id="cancel-reason-input"
            value={cancelledReason}
            onChange={(e) => setCancelledReason(e.target.value)}
            maxLength={CANCELLED_REASON_MAX_LENGTH}
            placeholder={`עד ${CANCELLED_REASON_MAX_LENGTH} תווים`}
            disabled={loading}
            style={{ width: '100%' }}
          />
          <div
            style={{
              fontSize: '12px',
              color: '#6b7280',
              marginTop: '4px',
              textAlign: 'left',
            }}
          >
            {trimmedReason.length}/{CANCELLED_REASON_MAX_LENGTH}
          </div>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px' }}>
            בחירת שירות תשלום:
          </label>
          <PaymentServicesTable
            key={tableKey}
            selectionMode="single"
            selectedService={selectedService}
            onSingleSelectionChange={setSelectedService}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'left',
            marginTop: '8px',
            paddingBottom: '10px',
          }}
        >
          <Button
            label="ביטול"
            onClick={handleHide}
            severity="secondary"
            disabled={loading}
          />
          <div style={{ width: '10px' }} />
          <Button
            label="אישור"
            onClick={handleApprove}
            loading={loading}
            disabled={isApproveDisabled}
            tooltip={approveTooltip}
            tooltipOptions={{ showOnDisabled: true, position: 'top' }}
          />
        </div>
      </div>
    </Dialog>
  );
};
